import React, { useState, useEffect, useRef } from 'react';
import { List, Grid } from 'lucide-react';
import CoOwnerModal from "../../components/CoOwnerModal";
import { fetchSharedAssistants } from '../../services/api/virtualAssistant';
import CustomModal from "../../components/CustomModal";
import AssistantPagination from './AssistantPagination';
import { Assistant as SharedAssistant } from "../../types/assistant";
import AssistantMergeModal from "../../components/AssistantMergeModal";
import SharedAssistantListView from './SharedAssistantListView';
import SharedAssistantGridView from './SharedAssistantGridView';
import { useSharedAssistantHandlers } from './useSharedAssistantHandlers';

interface SystemConfig {
  Id: string;
  ConfigValue: string;
}

const DEFAULT_PAGE_SIZE = 6;
const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000;

const getCachedData = (key: string) => {
  const item = sessionStorage.getItem(key);
  if (!item) return null;

  const { value, timestamp } = JSON.parse(item);
  if (Date.now() - timestamp > CACHE_DURATION) {
    sessionStorage.removeItem(key);
    return null;
  }
  return value;
};

const SharedVirtualAssistants: React.FC = () => {
  // Basic states
  const [assistants, setAssistants] = useState<SharedAssistant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showOptions, setShowOptions] = useState<{ [key: string]: boolean }>({});
  const optionsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [inference_url, setInferenceURL] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const hasTypedRef = useRef(false);

  const [systemConfig, setSystemConfig] = useState<SystemConfig[]>([]);

  useEffect(() => {
    const config = getCachedData('systemConfig');
    if (config) {
      setSystemConfig(config);
    }
  }, []);

  let KaiBlobLocation = systemConfig.find(item => item.Id === 'blob_root_folder')?.ConfigValue;
  const defaultAIFace = KaiBlobLocation + "/logo/boticondefault.svg";

  // Use the custom handlers hook
  const {
    customModal,
    setCustomModal,
    showMergeModal,
    setShowMergeModal,
    mergeTarget,
    setMergeTarget,
    deleteAfterMerge,
    setDeleteAfterMerge,
    selectedAssistant,
    isCoOwnerModalOpen,
    setIsCoOwnerModalOpen,
    selectedAppId,
    selectedAppOwner,
    selectedCoOwners,
    handleAssistantView,
    handleClone,
    handleExport,
    handleCopyAppUrl,
    handleMergeClick,
    handleMerge,
    handleCoOwner,
    handleSaveCoOwners
  } = useSharedAssistantHandlers(fetchSharedAssistantsData, currentPage, pageSize, searchTerm, inference_url);

  useEffect(() => {
    fetchSharedAssistantsData(1, pageSize, searchTerm);
  }, [pageSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(optionsRef.current).forEach((key) => {
        if (optionsRef.current[key] && !optionsRef.current[key]?.contains(event.target as Node)) {
          setShowOptions((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      hasTypedRef.current = true;
      fetchSharedAssistantsData(1, pageSize, searchTerm);
    }
    if (searchTerm.length === 0 && hasTypedRef.current) {
      hasTypedRef.current = false;
      fetchSharedAssistantsData(1, pageSize, searchTerm);
    }
  }, [searchTerm]);

  const getNetId = () => localStorage.getItem('netId') || '';

  async function fetchSharedAssistantsData(page: number, pageSize: number, searchTerm: string = '') {
    try {
      setLoading(true);
      const response = await fetchSharedAssistants(getNetId(), pageSize, (page - 1) * pageSize, searchTerm);
      setAssistants(response.assistants);
      setTotalPages(response.totalPages);
      setInferenceURL(response?.inference_url || 'http://localhost:4000/');
      setCurrentPage(page);
    } catch (err) {
      setError('Failed to fetch shared assistants');
    } finally {
      setLoading(false);
    }
  }

  // Create wrapped handlers that pass setShowOptions
  const wrappedHandleClone = (assistantId: string, assistantName: string) => {
    return handleClone(assistantId, assistantName, setShowOptions);
  };

  const wrappedHandleExport = (assistant: SharedAssistant) => {
    return handleExport(assistant, setShowOptions);
  };

  const wrappedHandleCopyAppUrl = (assistantId: string) => {
    return handleCopyAppUrl(assistantId, setShowOptions);
  };

  return (
    <div className="py-6">
      {/* Search Bar and View Toggle */}
      <div className="px-3 mb-4 flex items-center justify-between">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name"
          className="px-2 py-2 border border-gray-300 rounded-md w-96"
        />
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-md ${viewMode === "list" ? "bg-gray-300" : "bg-gray-100"}`}
          >
            <List size={18} />
          </button>
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2 rounded-md ${viewMode === "grid" ? "bg-gray-300" : "bg-gray-100"}`}
          >
            <Grid size={18} />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loader"></div>
        </div>
      ) : (
        <>
          {viewMode === "list" ? (
            <SharedAssistantListView
              assistants={assistants}
              handleAssistantView={handleAssistantView}
              showOptions={showOptions}
              setShowOptions={setShowOptions}
              optionsRef={optionsRef}
              handleCoOwner={handleCoOwner}
              handleCopyAppUrl={wrappedHandleCopyAppUrl}
              handleClone={wrappedHandleClone}
              handleExport={wrappedHandleExport}
              handleMergeClick={handleMergeClick}
              defaultAIFace={defaultAIFace}
            />
          ) : (
            <SharedAssistantGridView
              assistants={assistants}
              handleAssistantView={handleAssistantView}
              showOptions={showOptions}
              setShowOptions={setShowOptions}
              optionsRef={optionsRef}
              handleCoOwner={handleCoOwner}
              handleCopyAppUrl={wrappedHandleCopyAppUrl}
              handleClone={wrappedHandleClone}
              handleExport={wrappedHandleExport}
              handleMergeClick={handleMergeClick}
              defaultAIFace={defaultAIFace}
            />
          )}
          
          {/* Pagination */}
          {!loading && assistants.length > 0 && 
            <AssistantPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                searchTerm={searchTerm}
                onPageChange={(page) => fetchSharedAssistantsData(page, pageSize, searchTerm)}
               onPageSizeChange={(size) => {
                  setPageSize(size);
                  fetchSharedAssistantsData(1, size, searchTerm);
                }}
              />
          }
        </>
      )}

      {/* Modals */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal({ ...customModal, isOpen: false })}
        onConfirm={customModal.onConfirm}
        title={customModal.title}
        message={customModal.message}
        type={customModal.type}
      />

      {showMergeModal && selectedAssistant &&
        <AssistantMergeModal
          assistant={selectedAssistant}
          mergeTarget={mergeTarget}
          deleteAfterMerge={deleteAfterMerge}
          setMergeTarget={setMergeTarget}
          setDeleteAfterMerge={setDeleteAfterMerge}
          setShowMergeModal={setShowMergeModal}
          handleMerge={handleMerge}
          isDeleteEnabled={false}
        />}

      <CoOwnerModal
        isOpen={isCoOwnerModalOpen}
        onClose={() => setIsCoOwnerModalOpen(false)}
        assistantId={selectedAppId}
        appOwner={selectedAppOwner}
        onSave={handleSaveCoOwners}
        initialSelectedUsers={selectedCoOwners}
      />
    </div>
  );
};

export default SharedVirtualAssistants;